import memoize from 'fast-memoize';
import { ShopifyCurrency } from '../../models/ShopifyCurrency.model';
import { Color } from '../../enums/Color.enum';
import { ProductFeature } from '../../enums/ProductFeature.enum';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { ProductTag, ProductTags } from '../../enums/ProductTag.enum';
import { Fit } from '../../enums/Fit.enum';
import { ShopifyProduct, ShopifyProducts } from '../../models/ShopifyProduct.model';
import { FeaturedImage, ViewVariant } from '../../models/ShopifyVariant.model';
import { ImageNameData, ProductStyle, ViewProduct, ViewProducts } from '../../models/ViewProduct.model';
import { LongSize, ShortSize } from '../../product_detail/components/size.enum';
import { currencyToString, filterImagesByColor } from '../../util';
import { HoneyloveEnvironmentContext } from '../../models/HoneyloveWindow';
import { applyTimedSaleDiscount, applyWarehouseDiscount } from '../../discounts';
import { Denomination } from '../../product_detail/components/denomination.enum';

export const toViewProducts = (rawProducts: ShopifyProducts, currencyCode: string, environmentContext: HoneyloveEnvironmentContext): ViewProducts =>
  Object.keys(rawProducts || {}).reduce(
    (acc, handle) => ({ ...acc, [handle]: toViewProduct(rawProducts[handle as ProductHandle], currencyCode, environmentContext) }),
    {},
  ) as ViewProducts;

export const toViewProduct = memoize((rawProduct: ShopifyProduct, currencyCode: string, environmentContext: HoneyloveEnvironmentContext): ViewProduct => {
  const { timedSaleDiscounts } = environmentContext;
  const productPrice = rawProduct.price / 100; // TODO remove /100 when moving to next
  const displayPrice = currencyToString({ active: currencyCode } as ShopifyCurrency, productPrice);
  const warehousePrice = Math.round(applyWarehouseDiscount(rawProduct.price, rawProduct.type, environmentContext) / 100); // TODO remove /100 when moving to next

  const timedSalePrice = timedSaleDiscounts[rawProduct.handle]
    ? Math.round(applyTimedSaleDiscount(rawProduct.price, rawProduct.handle, environmentContext) / 100)
    : productPrice; // TODO remove /100 when moving to next

  return (
    rawProduct && {
      id: String(rawProduct.id),
      internalId: rawProduct.internalId ?? String(rawProduct.id), // TODO remove id fallback after moving to next
      title: rawProduct.title,
      handle: rawProduct.handle,
      coreFeatures: getCoreFeatures(rawProduct),
      features: getProductFeatures(rawProduct),
      fitRecommendationOneLiner: getFitRecommendationsOneLiner(rawProduct),
      fitRecommendation: getFitRecommendations(rawProduct),
      fabricDetails: getFabricDetails(rawProduct),
      description: getProductDescription(rawProduct),
      sizingWarning: getProductSizingWarning(rawProduct),
      sizingUrl: getSizingUrl(),
      price: productPrice,
      sku: variantSkuToProductSku(rawProduct.variants[0].sku),
      compareAtPrice: rawProduct.compare_at_price / 100,
      displayPrice,
      warehouseDisplayPrice: currencyToString({ active: currencyCode } as ShopifyCurrency, warehousePrice),
      timedSalePrice,
      timedSaleDisplayPrice: currencyToString({ active: currencyCode } as ShopifyCurrency, timedSalePrice),
      displayCompareAtPrice: currencyToString({ active: currencyCode } as ShopifyCurrency, rawProduct.compare_at_price / 100),
      colors: uniqueColors(rawProduct, findOptionIndex(rawProduct, 'Color')),
      sizes: uniqueSizes(rawProduct, findOptionIndex(rawProduct, 'Size')) as LongSize[],
      styles: getProductStyles(rawProduct),
      fits: getFits(rawProduct),
      denominations: getDenominations(rawProduct, findOptionIndex(rawProduct, 'Denominations')),
      variants: mapVariants(rawProduct, currencyCode),
      images: rawProduct.images,
      featuredImage: rawProduct.featured_image,
      tags: rawProduct.tags,
      vendor: rawProduct.vendor,
      type: rawProduct.type,
      published_at: rawProduct.published_at,
      shouldDisplayReviews: getReviewDisplayStatus(rawProduct),
      hiddenInMenus: getHiddenInMenusStatus(rawProduct),
      addOn: getAddOnStatus(rawProduct),
      parsedImages: parseImages(rawProduct),
      isReturnable: !(rawProduct.tags.includes('no_service') || (environmentContext.timedSaleActive && rawProduct.tags.includes('no_service_timed_sale'))),
      isReturnUnworn: rawProduct.tags.includes('return_unworn'),
      path: rawProduct.path,
      collection: getCollection(rawProduct),
    }
  );
});

export const getColor = (product: ViewProduct, assignedColor?: Color): Color => {
  const color = assignedColor ?? product?.colors?.[0];
  return product?.colors?.includes(color) ? color : product?.colors?.[0];
};

export const getSize = (product: ViewProduct, assignedSize?: LongSize): LongSize => {
  if (!assignedSize) return product?.sizes[0];
  if (product?.sizes?.includes(assignedSize)) return assignedSize;

  const isLargeSize = ![LongSize.XS, LongSize.Small].includes(assignedSize);

  return isLargeSize ? product?.sizes?.[product.sizes.length - 1] : product?.sizes?.[0];
};

const getSizingUrl = () => '#sizing-tool-open=true';

const getCoreFeatures = ({ handle }: ShopifyProduct) => {
  switch (handle) {
    case ProductHandle.EGiftCard:
      return 'Shopping for your loved ones and not sure what to give them? E-gift cards make the perfect gift for any special occasion. Personalize yours with a thoughtful message they are sure to love. ';
    case ProductHandle.QueenBrief:
      return 'An hourglass compression panel and smoothing brief bottom provide all-over shaping without sacrificing comfort or style.';
    case ProductHandle.LiftWearTank:
      return 'The LiftWear Tank is a key wardrobe staple with three main benefits - a built-in underwire-free molded bra, targeted back and tummy smoothing, in a cute design that can be worn on its own or under clothing. Warning - You might forget to take it off.';
    case ProductHandle.LiftWearCami:
      return 'Built-in bust support lifts without underwire, targeted smoothing holds your tummy, and adjustable straps let you customize your fit.';
    case ProductHandle.SuperPowerShort:
      return 'Targeted compression sculpts without squeezing, flexible boning prevents roll-down, and breathable fabric keeps you cool.';
    case ProductHandle.SuperPowerBrief:
      return 'Targeted compression sculpts your midsection, flexible boning prevents roll-down, and mesh fabric smooths your bottom.';
    case ProductHandle.SuperPowerThong:
      return 'Targeted compression sculpts your midsection, flexible boning prevents roll-down, and thong bottom eliminates panty lines.';
    case ProductHandle.VNeckBra:
      return 'Supportive microfabric, bonded bust support, and a hardware-free design will make you feel lifted, secure, and oh-so-smooth.';
    case ProductHandle.SilhouetteBra:
      return '3D printed velvet adds support, and a bonded underbust lifts without underwire. Adjustable straps and back closure let you modify your fit.';
    case ProductHandle.CrossoverBra:
      return 'Peekaboo mesh adds a sexy touch, and a bonded underbust lifts without underwire. Adjustable straps and back let you modify your fit.';
    case ProductHandle.BoldnessTank:
      return '360° compression panels and flexible boning provide maximum sculpting. Bonded underbust support lifts without underwire.';
    case ProductHandle.EverydayCottonThong:
      return 'If you want a little midsection hold with zero visibility underneath clothing, the Cotton Thong was made for you. The Thong’s stretchy cotton material and high waisted rise work together to gently smooth your midsection. The garment’s thong cut means you never have to worry about panty lines again. Plus, the Thong is extra breathable thanks to its 100% cotton gusset.';
    case ProductHandle.EverydayCottonBrief:
      return 'The Cotton Brief is the ultimate pair of underwear. The Brief’s stretchy cotton material and high waisted rise work together to gently smooth your midsection. Plus, the Brief is extra breathable thanks to its 100% cotton gusset.';
    case ProductHandle.WaistHeroCincher:
      return 'High quality steel boning contours your waist into an hourglass shape. Adjust to your desired level of compression.';
    case ProductHandle.EverReadyPant:
      return 'Interior shaping panels sculpt your tummy, hips, and booty. Tweed ponte fabric smooths and conceals. 29.38" inseam.';
    case ProductHandle.CamiBodysuit:
      return `Bust-to-booty smoothing that never comes untucked. Featuring underwire-free lift, adjustable straps, and a wedgie-free bottom.`;
    case ProductHandle.LeggingTwo:
      return `Featuring hidden shaping panels, an absorbent inner gusset, and sheer-proof fabric. Inseam is 26.25" for XS-L and 26.75" for XL-3X.`;
    case ProductHandle.BoldnessBodysuit:
      return '360° compression panels and flexible boning provide maximum sculpting. Featuring underwire-free lift and an adjustable gusset.';
    case ProductHandle.SilhouetteBrief:
      return '3D printed velvet piping adds support and style. High recovery stretch fabric moves with your body. Silky, sexy, and invisible under clothing.';
    case ProductHandle.SculptwaveCapri:
      return 'This capri’s full-length coverage and innovative construction make it completely invisible under clothing. If you crave a little extra coverage and powerful, targeted compression, this all-over sculpting garment will be your new go-to. Style with tight pants and dresses for a totally smooth look.';
    case ProductHandle.TankBodysuit:
      return `360° bonded compression smooths your tummy and hips. Built-in bust support lifts without underwire. Supportive straps lay gently on skin.`;
    default:
      return "Sculptwear's targeted compression works using panels of varying compression to shape where you want shaping and release everywhere else.";
  }
};

const getProductFeatures = ({ handle }: ShopifyProduct): ProductFeature[] => {
  switch (handle) {
    case ProductHandle.SuperPowerShort:
      return [ProductFeature.HighCompression, ProductFeature.WontRollDown, ProductFeature.SweatWicking, ProductFeature.BootyLifting];
    case ProductHandle.SuperPowerBrief:
      return [ProductFeature.HighCompression, ProductFeature.WontRollDown, ProductFeature.SweatWicking, ProductFeature.BootySmoothing];
    case ProductHandle.SuperPowerThong:
      return [ProductFeature.HighCompression, ProductFeature.NoPantyLines, ProductFeature.WontRollDown, ProductFeature.SweatWicking];
    case ProductHandle.QueenBrief:
      return [ProductFeature.MediumCompression, ProductFeature.WontRollDown, ProductFeature.SweatWicking, ProductFeature.BootySmoothing];
    case ProductHandle.LiftWearTank:
      return [ProductFeature.UnderwireFree, ProductFeature.TargetedSmoothing, ProductFeature.DigFreeStraps, ProductFeature.NoBackBulge];
    case ProductHandle.LiftWearCami:
      return [ProductFeature.UnderwireFree, ProductFeature.TargetedSmoothing, ProductFeature.AdjustableStraps, ProductFeature.NoBackBulge];
    case ProductHandle.VNeckBra:
      return [ProductFeature.UnderwireFree, ProductFeature.NoBackBulge, ProductFeature.BondedSupport];
    case ProductHandle.SilhouetteBra:
      return [ProductFeature.UnderwireFree, ProductFeature.AdjustableStraps, ProductFeature.PrintedSupport];
    case ProductHandle.BoldnessTank:
      return [ProductFeature.HighestCompression, ProductFeature.ThreeSixtyBoning, ProductFeature.UnderwireFree];
    case ProductHandle.CrossoverBra:
      return [ProductFeature.UnderwireFree, ProductFeature.AdjustableStraps, ProductFeature.PeekabooMesh];
    case ProductHandle.EverydayCottonThong:
      return [];
    case ProductHandle.EverydayCottonBrief:
      return [];
    case ProductHandle.CamiBodysuit:
      return [ProductFeature.AdjustableStraps, ProductFeature.TargetedSmoothing, ProductFeature.AdjustableGusset, ProductFeature.WedgieProof];
    case ProductHandle.WaistHeroCincher:
      return [ProductFeature.HighestCompression, ProductFeature.EasyOnOff, ProductFeature.FlexibleBoning];
    case ProductHandle.EverReadyPant:
      return [ProductFeature.HiddenShaping, ProductFeature.SmoothingFabric, ProductFeature.StretchWaistband, ProductFeature.WrinkleFree];
    case ProductHandle.LeggingTwo:
      return [ProductFeature.HiddenShaping, ProductFeature.SheerProofFabric, ProductFeature.NoPantyLines, ProductFeature.AdjustableGusset];
    case ProductHandle.TankBodysuit:
      return [ProductFeature.ThreeSixtyBonding, ProductFeature.AdjustableGusset, ProductFeature.UnderwireFree, ProductFeature.WedgieProof];
    case ProductHandle.BoldnessBodysuit:
      return [ProductFeature.HighestCompression, ProductFeature.ThreeSixtyBoning, ProductFeature.UnderwireFree, ProductFeature.AdjustableGusset];
    case ProductHandle.SilhouetteBrief:
      return [ProductFeature.HighRecoveryStretch, ProductFeature.PrintedSupport, ProductFeature.HighWaistCut, ProductFeature.WedgieProof];
    case ProductHandle.SculptwaveCapri:
      return []; // Discontinued
    case ProductHandle.EGiftCard:
      return [ProductFeature.GreatGift, ProductFeature.EmailDelivery];
    default:
      return [];
  }
};

const getFitRecommendationsOneLiner = ({ handle }: ShopifyProduct): string => {
  switch (handle) {
    case ProductHandle.SuperPowerShort:
      return 'True to size. If you have proportionally bigger hips, size up.';
    case ProductHandle.SuperPowerBrief:
      return 'Fits true to size.';
    case ProductHandle.SuperPowerThong:
      return 'Fits true to size.';
    case ProductHandle.QueenBrief:
      return 'Fits true to size.';
    case ProductHandle.LiftWearTank:
      return 'Size up for extra length. Size down for a tighter fit.';
    case ProductHandle.LiftWearCami:
      return 'Size up for extra length. Size down for a tighter fit.';
    case ProductHandle.VNeckBra:
      return "Size down if you're between sizes.";
    case ProductHandle.SilhouetteBra:
      return "Size down if you're between sizes.";
    case ProductHandle.BoldnessTank:
      return 'Size down for more waist compression, especially if you have a small underband and a larger bust.';
    case ProductHandle.CrossoverBra:
      return "Size down if you're between sizes.";
    case ProductHandle.EverydayCottonThong:
      return "Size down if you're between sizes or prefer a tighter fit.";
    case ProductHandle.EverydayCottonBrief:
      return "Size down if you're between sizes or prefer a tighter fit.";
    case ProductHandle.WaistHeroCincher:
      return 'Use the sizing tool to find your best fit.';
    case ProductHandle.EverReadyPant:
      return '';
    case ProductHandle.LeggingTwo:
      return '';
    case ProductHandle.BoldnessBodysuit:
      return 'Use your bra size to determine your size in the Bodysuit.';
    case ProductHandle.SilhouetteBrief:
      return '';
    case ProductHandle.CamiBodysuit:
      return 'Runs true to size. Size down for petites.';
    case ProductHandle.SculptwaveCapri:
      return "If you're in between sizes, size down.";
    case ProductHandle.TankBodysuit:
      return ``;
    default:
      return '';
  }
};

const getFitRecommendations = ({ handle }: ShopifyProduct): string => {
  switch (handle) {
    case ProductHandle.SuperPowerShort:
      return "If you have a wider thigh or proportionally bigger hips, we recommend sizing up so you'll have enough length in the torso, and the X panel will hit at the appropriate place on your body. If you have a longer torso or need additional length, you can use the optional straps included with your purchase to help the garment reach your bra line.";
    case ProductHandle.SuperPowerBrief:
      return 'If you have a longer torso or need additional length, you can use the optional straps included with your purchase to help the garment reach your bra line.';
    case ProductHandle.SuperPowerThong:
      return 'If you have a longer torso or need additional length, you can use the optional straps included with your purchase to help the garment reach your bra line.';
    case ProductHandle.QueenBrief:
      return "If you're between sizes, size down for a tighter fit. If you have a longer torso or need additional length, you can use the optional straps included with your purchase to help the garment reach your bra line.";
    case ProductHandle.LiftWearTank:
      return "If you have a wider hip or prefer a longer shirt length, we recommend sizing up. If you're between sizes or want more compression in the stomach, size down. The bust features removable cups so you can control your level of coverage.";
    case ProductHandle.LiftWearCami:
      return "If you have a wider hip or prefer a longer shirt length, we recommend sizing up. If you're between sizes or want more compression in the stomach, size down. Loosen the straps to give yourself more strap length, or tighten the straps for increased support. Cross the straps in the back for even more bust support. The bust features removable cups so you can control your level of coverage.";
    case ProductHandle.VNeckBra:
      return "Removable cups are included for modesty and to give you a little extra shape. If you're looking for an option with slightly less coverage, try our Silhouette Bra. ";
    case ProductHandle.SilhouetteBra:
      return 'Removable cups with a slightly thicker apex are included for modesty and to give you a little extra shape. This bra features a lower neckline and medium coverage. For more coverage, try our V-Neck Bra.';
    case ProductHandle.BoldnessTank:
      return `This is a highly compressive garment that must be stepped into rather than pulling over your head. If you have a pear shape it may be difficult to pull over your hips, in which case we suggest you go a size above our suggestion or your usual size.`;
    case ProductHandle.CrossoverBra:
      return 'Removable cups with a slightly thicker apex are included for modesty and to give you a little extra shape. This bra features a lower neckline and medium coverage. For more coverage, try our V-Neck Bra.';
    case ProductHandle.EverydayCottonThong:
      return "The Thong is designed to have longer side coverage, and the top of the garment should sit at or just below your natural waist. To get more support out of the Thong, we've selected a fabric with a stronger knit to provide more hold, as opposed to traditional underwear that stretches out on the body. The Thong is cut generously, so if you're between sizes or prefer a tighter fit, we recommend sizing down.";
    case ProductHandle.EverydayCottonBrief:
      return "The Brief is designed to have fuller back coverage and longer side coverage, and the top of the garment should sit at or just below your natural waist. To get more support out of the Brief, we've selected a fabric with a stronger knit to provide more hold, as opposed to traditional underwear that stretches out on the body. The Briefs are cut generously, so if you're between sizes or prefer a tighter fit, we recommend sizing down.";
    case ProductHandle.WaistHeroCincher:
      return "This garment is built with less stretch than most Honeylove garments, so if you're in between sizes, we strongly recommend sizing up. The Cincher features three rows of hook and eyes so that you can make it tighter or looser based on weight fluctuations, but we still recommend choosing the larger of two sizes you are considering. This garment also features a zipper closure for easy on and off.";
    case ProductHandle.EverReadyPant:
      return `This pant fits true to size, but we always recommend using our Sizing Help button above the size selector for the best fit.`;
    case ProductHandle.LeggingTwo:
      return `Size down if you're between sizes or prefer a tighter fit.`;
    case ProductHandle.BoldnessBodysuit:
      return `This is a highly compressive garment that must be stepped into rather than pulling over your head. If you have a pear shape it may be difficult to pull over your hips, in which case we suggest you go a size above our suggestion or your usual size.`;
    case ProductHandle.SilhouetteBrief:
      return "Size down if you're between sizes or prefer a tighter fit.";
    case ProductHandle.CamiBodysuit:
      return `We always recommend using our Sizing Help button above the size selector. If you know your size in our LiftWear Tank or Cami we suggest ordering the same size. We recommend ordering the petite size if you are 5’3’ or less or if you are 5’4’ and have a short torso.`;
    case ProductHandle.SculptwaveCapri:
      return "If you're in between sizes or looking for more compression, we recommend sizing down. If you're looking for a product with more compression, try our SuperPower Short.";
    case ProductHandle.TankBodysuit:
      return `We always recommend using our Sizing Help button above the size selector. If you know your size in our LiftWear Tank or Cami we recommend ordering the same size.`;
    default:
      return '';
  }
};

const getFabricDetails = ({ handle }: ShopifyProduct): string[] => {
  switch (handle) {
    case ProductHandle.SuperPowerShort:
      return ['Body: 75% Nylon, 25% Spandex', 'Lining: 81% Nylon, 19% Spandex', 'Gusset: 100% Cotton'];
    case ProductHandle.SuperPowerBrief:
      return ['Body: 75% Nylon, 25% Spandex', 'Lining: 81% Nylon, 19% Spandex', 'Gusset: 100% Cotton'];
    case ProductHandle.SuperPowerThong:
      return ['Body: 75% Nylon, 25% Spandex', 'Lining: 81% Nylon, 19% Spandex', 'Gusset: 100% Cotton'];
    case ProductHandle.QueenBrief:
      return ['Body: 65% Nylon, 35% Spandex', 'Lining: 80% Nylon, 20% Spandex', 'Gusset: 100% Cotton'];
    case ProductHandle.LiftWearTank:
      return ['Body: 62% Nylon, 38% Spandex', 'Mesh: 78% Nylon, 22% Spandex', 'Bra cup: 91% Nylon, 9% Spandex'];
    case ProductHandle.LiftWearCami:
      return ['Body: 62% Nylon, 38% Spandex', 'Mesh: 78% Nylon, 22% Spandex', 'Bra cup: 91% Nylon, 9% Spandex'];
    case ProductHandle.VNeckBra:
      return ['Body: 62% Nylon, 38% Spandex', 'Bra cup: 91% Nylon, 9% Spandex'];
    case ProductHandle.SilhouetteBra:
      return ['Body: 62% Nylon, 38% Spandex', 'Bra cup: 91% Nylon, 9% Spandex'];
    case ProductHandle.BoldnessTank:
      return ['Body/Corps: 62% Nylon, 38% Spandex', 'Bra cup: 91% Nylon,  9% Spandex'];
    case ProductHandle.CrossoverBra:
      return ['Body/Corps: 62% Nylon, 38% Spandex', 'Mesh: 64.4% Nylon, 35.6% Spandex', 'Lining: 100% Polyester'];
    case ProductHandle.EverydayCottonThong:
      return ['Body: 85% Cotton, 15% Spandex', 'Gusset: 100% Cotton'];
    case ProductHandle.EverydayCottonBrief:
      return ['Body: 85% Cotton, 15% Spandex', 'Gusset: 100% Cotton'];
    case ProductHandle.WaistHeroCincher:
      return ['Body: 60% Nylon, 40% Spandex', 'Lining: 64% Spandex, 36% Nylon'];
    case ProductHandle.EverReadyPant:
      return ['Body: 48% Rayon, 33% Nylon, 13% Polyester, 6% Spandex'];
    case ProductHandle.LeggingTwo:
      return ['Body: 82% Polyester, 18% Spandex', 'Other: 70% Nylon, 30% Spandex'];
    case ProductHandle.BoldnessBodysuit:
      return ['Body: 62% Nylon, 38% Spandex', 'Bra cup: 91% Nylon, 9% Spandex', 'Gusset: 100% Cotton'];
    case ProductHandle.SilhouetteBrief:
      return ['Body: 50% Nylon, 50% Lycra'];
    case ProductHandle.SculptwaveCapri:
      return ['Body: 55% Nylon, 45% Spandex', 'Lining:  69% Nylon, 31% Spandex', 'Gusset: 100% Cotton'];
    case ProductHandle.TankBodysuit:
      return ['Body: 62% Nylon, 38% Spandex', 'Mesh: 78% Nylon, 22% Spandex', 'Bra cup: 91% Nylon, 9% Spandex', 'Gusset: 100% Cotton'];
    default:
      return [];
  }
};

const getProductDescription = ({ handle }: ShopifyProduct): string => {
  switch (handle) {
    case ProductHandle.QueenBrief:
      return 'Top comfort';
    case ProductHandle.EGiftCard:
      return 'Perfect gift';
    case ProductHandle.SuperPowerShort:
      return 'The original';
    case ProductHandle.LiftWearTank:
      return 'Lifting & smoothing';
    case ProductHandle.SuperPowerThong:
      return 'No panty lines';
    case ProductHandle.VNeckBra:
      return 'Invisible lift';
    case ProductHandle.LiftWearCami:
      return 'Adjustable support';
    case ProductHandle.SilhouetteBra:
      return 'Adjustable lift';
    case ProductHandle.BoldnessTank:
      return 'Sculpted and stylish';
    case ProductHandle.CrossoverBra:
      return 'Stylish support';
    case ProductHandle.EverydayCottonThong:
      return 'Invisible hold';
    case ProductHandle.EverydayCottonBrief:
      return 'Essential undies';
    case ProductHandle.WaistHeroCincher:
      return 'Waist defining';
    case ProductHandle.EverReadyPant:
      return 'Hidden sculpting';
    case ProductHandle.LeggingTwo:
      return 'No undies needed';
    case ProductHandle.CamiBodysuit:
      return 'Smooth and lifted';
    case ProductHandle.BoldnessBodysuit:
      return 'Body contouring';
    case ProductHandle.SilhouetteBrief:
      return 'Everyday beautiful';
    case ProductHandle.TankBodysuit:
      return 'Sleek and supportive';
    case ProductHandle.SculptwaveCapri: // Blank in sheet
    case ProductHandle.SuperPowerBrief:
    default:
      return 'Everyday shaper';
  }
};

const variantSkuToProductSku = (sku: string) => (sku?.includes('-') ? sku.split('-')[0] : sku);
const findOptionIndex = (rawProduct: ShopifyProduct, key: string) => rawProduct.options.findIndex((item) => item === key);
const uniqueColors = (rawProduct: ShopifyProduct, optionIndex: number) =>
  rawProduct.variants.map(({ options }) => options[optionIndex]).filter(uniqueFilterFn) as Color[];

const uniqueSizes = (rawProduct: ShopifyProduct, optionIndex: number) => rawProduct.variants.map(({ options }) => options[optionIndex]).filter(uniqueFilterFn);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const uniqueFilterFn = (value: any, index: number, self: any) => {
  return !!value && self.indexOf(value) === index;
};

const getFits = (product: ShopifyProduct): Fit[] => {
  return product.variants.reduce((acc, curr) => {
    return !curr.fit || acc.includes(curr.fit) ? acc : [...acc, curr.fit];
  }, [] as Fit[]);
};

const getDenominations = (product: ShopifyProduct, optionIndex: number): Denomination[] =>
  product.variants.map((v) => v.options[optionIndex] as Denomination).filter(uniqueFilterFn);

const getProductStyles = ({ handle }: ShopifyProduct): ProductStyle[] => {
  switch (handle) {
    case ProductHandle.LeggingTwo:
      return [
        { handle: ProductHandle.LeggingTwo, label: 'Legging 2.0', title: 'Legging 2.0' },
        { handle: ProductHandle.EverReadyPant, label: 'EverReady Pant', title: 'EverReady Pant' },
      ];
    case ProductHandle.EverReadyPant:
      return [
        { handle: ProductHandle.LeggingTwo, label: 'Legging 2.0', title: 'Legging 2.0' },
        { handle: ProductHandle.EverReadyPant, label: 'EverReady Pant', title: 'EverReady Pant' },
      ];
    case ProductHandle.LiftWearTank:
      return [
        { handle: ProductHandle.LiftWearTank, label: 'Wide straps', title: 'Tank' },
        { handle: ProductHandle.LiftWearCami, label: 'Adjustable straps', title: 'Cami' },
      ];
    case ProductHandle.LiftWearCami:
      return [
        { handle: ProductHandle.LiftWearTank, label: 'Wide straps', title: 'Tank' },
        { handle: ProductHandle.LiftWearCami, label: 'Adjustable straps', title: 'Cami' },
      ];
    case ProductHandle.EverydayCottonBrief:
      return [
        { handle: ProductHandle.EverydayCottonBrief, label: 'Brief', title: 'Brief' },
        { handle: ProductHandle.EverydayCottonThong, label: 'Thong', title: 'Thong' },
      ];
    case ProductHandle.EverydayCottonThong:
      return [
        { handle: ProductHandle.EverydayCottonBrief, label: 'Brief', title: 'Brief' },
        { handle: ProductHandle.EverydayCottonThong, label: 'Thong', title: 'Thong' },
      ];
    // BEGIN style selections added for AB2021-10 test
    case ProductHandle.SuperPowerShort:
      return [
        { handle: ProductHandle.SuperPowerShort, label: 'Shorts', title: 'Shorts' },
        { handle: ProductHandle.SuperPowerBrief, label: 'Briefs', title: 'Briefs' },
        { handle: ProductHandle.SuperPowerThong, label: 'Thong', title: 'Thong' },
      ];
    case ProductHandle.SuperPowerBrief:
      return [
        { handle: ProductHandle.SuperPowerShort, label: 'Shorts', title: 'Shorts' },
        { handle: ProductHandle.SuperPowerBrief, label: 'Briefs', title: 'Briefs' },
        { handle: ProductHandle.SuperPowerThong, label: 'Thong', title: 'Thong' },
      ];
    case ProductHandle.SuperPowerThong:
      return [
        { handle: ProductHandle.SuperPowerShort, label: 'Shorts', title: 'Shorts' },
        { handle: ProductHandle.SuperPowerBrief, label: 'Briefs', title: 'Briefs' },
        { handle: ProductHandle.SuperPowerThong, label: 'Thong', title: 'Thong' },
      ];
    // BEGIN style selections added for AB2021-12 test
    case ProductHandle.SilhouetteBra:
      return [
        { handle: ProductHandle.SilhouetteBra, label: 'Silhouette', title: 'Silhouette' },
        { handle: ProductHandle.CrossoverBra, label: 'CrossOver', title: 'CrossOver' },
        { handle: ProductHandle.VNeckBra, label: 'V-neck', title: 'V-neck' },
      ];
    case ProductHandle.CrossoverBra:
      return [
        { handle: ProductHandle.SilhouetteBra, label: 'Silhouette', title: 'Silhouette' },
        { handle: ProductHandle.CrossoverBra, label: 'CrossOver', title: 'CrossOver' },
        { handle: ProductHandle.VNeckBra, label: 'V-neck', title: 'V-neck' },
      ];
    case ProductHandle.VNeckBra:
      return [
        { handle: ProductHandle.SilhouetteBra, label: 'Silhouette', title: 'Silhouette' },
        { handle: ProductHandle.CrossoverBra, label: 'CrossOver', title: 'CrossOver' },
        { handle: ProductHandle.VNeckBra, label: 'V-neck', title: 'V-neck' },
      ];
    // END style selections added for AB2021-12 test
    default:
      return [];
  }
};

const mapVariants = (rawProduct: ShopifyProduct, currencyCode: string): ViewVariant[] => {
  const productParsedImages = parseImages(rawProduct);
  return rawProduct.variants.map((variant) => {
    const variantPrice = variant.price / 100;
    const color = variant.options[findOptionIndex(rawProduct, 'Color')] as Color;
    const parsedImages = productParsedImages.filter((parsedImage) => parsedImage.color === color);
    const viewVariant: ViewVariant = {
      id: String(variant.id),
      sku: variant.sku,
      name: variant.name,
      available: variant.available,
      skuAvailability: variant.skuAvailability,
      barcode: variant.barcode,
      color,
      parsedImages,
      denomination: rawProduct.handle === ProductHandle.EGiftCard ? (variant.options[0] as Denomination) : undefined,
      size: variant.options[findOptionIndex(rawProduct, 'Size')],
      public_title: variant.public_title,
      featured_image: variant.featured_image || defaultFeaturedImage(rawProduct, color),
      fit: variant.fit || null,
      price: variantPrice,
      displayPrice: currencyToString({ active: currencyCode } as ShopifyCurrency, variantPrice),
    };

    return viewVariant;
  });
};

const defaultFeaturedImage = (rawProduct: ShopifyProduct, color: Color): FeaturedImage => {
  const src = filterImagesByColor(color, rawProduct.images)[0];
  return { src, product_id: rawProduct.id };
};

const parseImages = (rawProduct: ShopifyProduct): ImageNameData[] => {
  const variantRegex = /([a-z0-9]+)-([a-z_]+)-([a-z0-9]+)-([a-z]*)-([a-z]*)-?(\d?)_?.*.(jpg|png|jpeg)/gi;
  const modelImageAnglePriority = ['Front', 'Back', 'Side'];
  return rawProduct.images.reduce((acc: ImageNameData[], imagePath: string, imageIndex: number) => {
    variantRegex.exec(''); // Reset the regex
    const matchAllResult = variantRegex.exec(imagePath);

    if (matchAllResult && matchAllResult.length === 8) {
      const [imageName, _product, imageProductColorRaw, imageProductSize, imageProductModelName, imageProductAngle, imageAngleVariant = 0] = matchAllResult;
      const imageProductColor = imageProductColorRaw?.match(/[A-Z][a-z]+/g)?.join(' ');

      return [
        ...acc,
        {
          src: imagePath,
          imageName,
          imageIndex,
          size: imageProductSize as ShortSize,
          color: imageProductColor as Color,
          modelName: imageProductModelName,
          priority: 3 - modelImageAnglePriority.indexOf(imageProductAngle),
          imageAngleVariant: +imageAngleVariant,
          displayKey: `${imageProductModelName}, ${imageProductSizeToFullName(imageProductSize)}`,
        },
      ];
    }
    return acc;
  }, []);
};

const imageProductSizeToFullName = (shortName: string) => {
  switch (shortName) {
    case 'S':
      return 'Small';
    case 'M':
      return 'Medium';
    case 'L':
      return 'Large';
    default:
      return shortName;
  }
};

const getReviewDisplayStatus = ({ handle }: ShopifyProduct): boolean => {
  switch (handle) {
    case 'e-gift-card':
      return false;
    default:
      return true;
  }
};

const getHiddenInMenusStatus = (product: ShopifyProduct): boolean => {
  return product.tags.includes('hidden_in_menus');
};

const getAddOnStatus = (product: ShopifyProduct): boolean => {
  return product.tags.includes('add_on');
};

const getProductSizingWarning = ({ handle }: ShopifyProduct) => {
  switch (handle) {
    case ProductHandle.WaistHeroCincher:
      return '*This garment is built with limited stretch. We recommend sizing up in this product.';
    default:
      return '';
  }
};

const getCollection = (product: ShopifyProduct): ProductTag => {
  const collectionTags = ProductTags.filter((v) => v.startsWith('collection_')) as ProductTag[];
  return collectionTags.find((v) => product.tags.includes(v)) || 'hidden_in_menus';
};
